import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { DialogSize, GaMessageService, IDialog, IDialogButton } from '../../../../shared/message/ga-message.service';
import { IAddDocumentModel, IDocumentTypeModel, OrderEntryAddDocumentDialogComponent, OTHER_DOCUMENTS_TYPE } from './order-entry-add-document-dialog.component';
import { CellClassParams, ColDef, GridOptions, RowGroupOpenedEvent, RowHeightParams, RowNode } from 'ag-grid-community';
import { IDocumentGroup, IOrderEntryDocument, OrderEntryDocumentsDataService } from './order-entry-documents-data.service';
import { OrderEntryDocumentsService } from './order-entry-documents.service';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import _ from 'underscore';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { ITooltipExtendedParams } from '../../../../shared/ag-grid-components/ag-tooltip.component';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { IUserData } from '../../../../shared/services/auth/auth-data.service';
import moment from 'moment';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '../../../../shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { IActionButtonGrid, IActionButtonRendererParams } from '../../../../shared/ag-grid-components/ag-action-button/ag-action-button-renderer.component';
import { CellRendererComp } from '../../../../shared/ag-grid-components/ga-ag-grid.component';
import { AgGridDomLayout, GaAgGridService } from '../../../../shared/ag-grid-components/ga-ag-grid.service';
import { AgDocumentDetailRendererComponent } from '@app/main/order/entry/documents/ag-document-detail-renderer.component';
import { GRID_ORDER_ENTRY_DOCUMENTS } from '@app/shared/app-grids';

const GRID_TYPE_COLUMN:string = 'type';
const GRID_DOCUMENTS_COLUMN:string = 'documents';
const GRID_ACTION_COLUMN:string = 'action';

@Component({
  selector: 'order-entry-documents',
  template: `
    <h2 *ngIf="loading">
      <activity-indicator toLoad="ORDERENTRY.DOCS.TITLE"></activity-indicator>
    </h2>

    <div *ngIf="!loading">
      <h2 *ngIf="showTitle" class="no-margin-top" translate>ORDERENTRY.DOCS.TITLE</h2>

      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit"></ga-ag-grid>

      <div class="form-buttons" *ngIf="showUpload">
        <div class="button-group">
          <button class="btn btn-primary" (click)="addDocument()" translate [translateParams]="{ label: ('ORDERENTRY.DOCS.HEADERS.DOCUMENT' | translate) }">GA.UPLOADS.UPLOAD_LABEL</button>
        </div>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  styleUrls: [ './order-entry-documents.scss' ]
})
export class OrderEntryDocumentsComponent implements OnInit {
  @Input() public orderId:string;
  @Input() public editable:boolean;
  @Input() public showTitle:boolean;
  @Input() public showUpload:boolean;

  public gridId:string = GRID_ORDER_ENTRY_DOCUMENTS;
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public loading:boolean;

  private addDocumentDialog:IDialog;
  private confirmDeleteDialog:IDialog;
  private allowedDocuments:IDocumentTypeModel[] = [];

  constructor(
    private messageService:GaMessageService,
    private orderEntryDocumentsDataService:OrderEntryDocumentsDataService,
    private orderEntryDocumentsService:OrderEntryDocumentsService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private authService:AuthService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    // load documents
    this.loadInitDocuments();

    // register listener
    this.listen();
  }

  private listen():void {
    this.orderEntryDocumentsService.onDocumentChange.subscribe((docs:IDocumentGroup[]) => {
      if (docs?.length) {
        this.allowedDocuments = this.getAllowedDocuments(docs);

        // when grid is ready then set row data and refresh grid rows
        this.gaAgGridService.setRowData(this.gridId, docs, true);
      }
    });
  }

  private loadInitDocuments():void {
    if (this.orderEntryDocumentsService.documents?.length > 0) {
      return this.installGrid();
    }

    if (!this.orderEntryDocumentsService.documents?.length && this.orderEntryWebshopService.selectedShop) {
      this.loading = true;

      this.orderEntryDocumentsDataService.documents(
        this.orderEntryWebshopService.selectedShop?.id,
        this.orderEntryWebshopService.selectedCountry?.id,
        Number(this.orderId) || undefined,
        this.orderEntryWebshopService.shopContext?.bp?.location?.companyCountry?.id
      )
        .then((results:IDocumentGroup[]) => {
          this.orderEntryDocumentsService.documents = results;
          this.installGrid();
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  private getDefaultColDefs():ColDef[] {
    return [
      {
        field: GRID_TYPE_COLUMN,
        headerName: 'ORDERENTRY.DOCS.HEADERS.TYPE',
        cellRenderer: CellRendererComp.GROUP_CELL_RENDERER,
        cellRendererParams: {
          innerRenderer: CellRendererComp.RICH_TEXT_RENDERER,
          innerRendererParams: <IRichTextRendererParams>{
            type: RichTextType.TEXT,
            getValues(params:IRichTextRendererParams):IRichTextGrid {
              return {
                labelClass: 'bold',
                iconClass: (params:IRichTextRendererParams):string => {
                  if (params.node.data?.error?.message) {
                    return 'fa-warning error';
                  }
                  return '';
                },
                iconPosition: 'right',
                label: params.node.data?.type
              };
            }
          }
        },
        tooltipField: GRID_TYPE_COLUMN,
        tooltipComponent: CellRendererComp.TOOLTIP,
        tooltipComponentParams: {
          message: (params:ITooltipExtendedParams) => this.translationService.instant(params.node.data?.error?.message),
          tooltipShowExpression: (params:ITooltipExtendedParams) => !_.isUndefined(params.node.data?.error)
        }
      },
      {
        field: GRID_DOCUMENTS_COLUMN,
        headerName: 'ORDERENTRY.DOCS.HEADERS.DOCUMENT',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            return _.map(params.node.data?.documents, (doc:IOrderEntryDocument):IRichTextGrid => {
              return {
                href: this.orderEntryDocumentsService.getDocumentUrl(doc, params),
                iconClass: ():string => {
                  if (doc.warning?.message) {
                    return 'fa-warning warning';
                  } else {
                    return 'fa-download';
                  }
                },
                label: doc.name
              };
            });
          }
        },
        autoHeight: true,
        cellClass: this.getCellClass,
        tooltipField: GRID_DOCUMENTS_COLUMN,
        tooltipComponent: CellRendererComp.TOOLTIP,
        tooltipComponentParams: {
          message: (params:ITooltipExtendedParams):string => {
            const doc:IOrderEntryDocument = _.find(params.node.data?.documents, (doc:IOrderEntryDocument) => doc.warning?.message);
            return this.translationService.instant(doc.warning?.message);
          },
          tooltipShowExpression: (params:ITooltipExtendedParams) => !!_.find(params.node.data?.documents, (doc:IOrderEntryDocument) => doc.warning?.message)
        }
      },
      {
        field: 'date',
        headerName: 'ORDERENTRY.DOCS.HEADERS.DATE_ADDED',
        autoHeight: true,
        cellRenderer: 'agRichTextRenderer',
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            return _.map(params.node.data?.documents, (doc:IOrderEntryDocument):IRichTextGrid => {
              return {
                label: this.formattingService.formatDateMedium(doc.date) || '&nbsp;'
              };
            });
          }
        },
        cellClass: this.getCellClass
      },
      {
        field: 'author',
        headerName: 'ORDERENTRY.DOCS.HEADERS.ADDED_BY',
        autoHeight: true,
        cellRenderer: 'agRichTextRenderer',
        cellClass: this.getCellClass,
        cellRendererParams: {
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            return _.map(params.node.data?.documents, (doc:IOrderEntryDocument):IRichTextGrid => {
              return {
                label: `${doc.author?.firstname || '&nbsp;'} ${doc.author?.lastname || '&nbsp;'}`
              };
            });
          }
        }
      }
    ];
  }

  private installGrid():void {
    const colDefs:ColDef[] = _.map(this.getDefaultColDefs(), (colDef:ColDef) => {
      if (colDef.headerName) {
        colDef.headerName = this.translationService.instant(colDef.headerName);
      }
      colDef.flex = 1;
      colDef.suppressHeaderMenuButton = true;
      colDef.resizable = true;

      return colDef;
    });

    if (this.editable) {
      colDefs.push({
        field: GRID_ACTION_COLUMN,
        headerName: '',
        resizable: false,
        cellRenderer: CellRendererComp.ACTION_BUTTON_RENDERER,
        cellRendererParams: {
          getActions: (params:IActionButtonRendererParams) => {
            return _.map(params.node.data?.documents, (doc:IOrderEntryDocument):IActionButtonGrid => {
              return {
                enabled: doc.deleteAllowed,
                buttons: [
                  {
                    buttonClass: 'btn-primary btn-minimal btn-sm',
                    iconClass: 'fa-trash',
                    execute: (params:IActionButtonRendererParams, index:number):void => {
                      this.confirmDeleteDocument(params.node.data?.type, params.node.data?.documents[ index ]);
                    },
                    showExpression: (params:IActionButtonRendererParams, index:number):boolean => {
                      return params.node.data?.documents?.[ index ]?.deleteAllowed && params.context.editable;
                    }
                  }
                ]
              };
            });
          }
        },
        cellClass: this.getCellClass,
        pinned: 'right',
        suppressHeaderMenuButton: true
      });
    }

    this.gridOptions = {
      columnDefs: colDefs,
      rowData: this.orderEntryDocumentsService.documents,
      masterDetail: true,
      headerHeight: 42,
      domLayout: AgGridDomLayout.AUTO_HEIGHT,
      components: {
        agDocumentDetailRenderer: AgDocumentDetailRendererComponent
      },
      suppressContextMenu: true,
      detailCellRenderer: 'agDocumentDetailRenderer',
      detailCellRendererParams: {
        webshopContext: this.orderEntryWebshopService
      },
      onGridReady: this.onGridReady,
      tooltipShowDelay: 0,
      context: {
        deleteDocument: this.confirmDeleteDocument,
        addDocument: this.addDocument,
        editable: this.editable,
        orderId: this.orderId,
        getDocumentUrl: this.orderEntryDocumentsService.getDocumentUrl
      },
      animateRows: false,
      onRowGroupOpened: (event:RowGroupOpenedEvent) => {
        event.api.redrawRows();
      },
      getRowHeight: (params:RowHeightParams) => {
        /**
         * Returning undefined value will ensure its default behavior which is using auto height, this applies to row node (level 0) or detail node (level 1)
         * Moreover, if the current node row height is the same with the newly set row height, then calculation won't happen again.
         * AG-grid doesn't provide event whenever row detail is expanded, otherwise the calculation should be placed on this event.
         */
        if (params.node.detail) {
          const rowHeight:number = $(`.ag-row-level-1[row-index="${params.node.rowIndex}"]`).children().outerHeight() || 0;

          if (params.node.rowHeight !== rowHeight) {
            setTimeout(() => {
              params.node.setRowHeight(rowHeight);
            });
          }
        }

        return undefined;
      }
    };

    this.gridInit = true;
  }

  private onGridReady = ():void => {
    if (this.editable) {
      this.gaAgGridService.sizeColumnsToFit(this.gridId);
      this.gaAgGridService.autoSizeColumns(this.gridId, [GRID_ACTION_COLUMN]);
    }

    this.gaAgGridService.forEachNode(this.gridId, (rowNode:RowNode) => {
      if (rowNode.data?.needsDocument) {
        rowNode.setExpanded(true);
      }
    });
  };

  public addDocument = (document?:IDocumentGroup):void => {
    this.addDocumentDialog = this.messageService.showDialog({
      component: OrderEntryAddDocumentDialogComponent,
      inputs: {
        preselectedDocument: document,
        allowedDocuments: this.allowedDocuments,
        orderId: this.orderId
      },
      outputs: {
        onNewDocumentAdded: this.onNewDocumentAdded,
        onCancel: ():void => this.addDocumentDialog.hide()
      },
      wrapperComponent: false,
      size: DialogSize.MD
    });
  };

  private confirmDeleteDocument = (type:string, doc:IOrderEntryDocument):void => {
    const title:string = this.translationService.instant('ORDERENTRY.DOCS.DELETE', { document: doc.name });
    const message:string = `
      <p>${this.translationService.instant('ORDERENTRY.DOCS.DELETE_TEXT_1', { document: doc.name })}</p>
      <p>${this.translationService.instant('ORDERENTRY.DOCS.DELETE_TEXT_2')}</p>
    `;
    const buttons:IDialogButton[] = [
      {
        label: 'GA.BUTTONS.DELETE',
        className: 'btn-danger',
        id: 'delete',
        onClick: () => {
          this.deleteDocument(type, doc.id);
          this.confirmDeleteDialog.hide();
        }
      },
      {
        label: 'GA.BUTTONS.CANCEL',
        className: 'btn-default'
      }
    ];

    this.confirmDeleteDialog = this.messageService.showMessageDialog(title, message, buttons);
  };

  private deleteDocument = (type:string, documentId:string | number):void => {
    let documents:IDocumentGroup[] = _.clone(this.orderEntryDocumentsService.documents);
    const docType:IDocumentGroup = _.findWhere(documents, { type: type });
    const docIndex:number = _.findIndex(docType.documents, { id: documentId });

    if (docType.documents?.length === 1) {
      if (docType.id === OTHER_DOCUMENTS_TYPE) {
        documents = _.reject(documents, (doc:IDocumentGroup) => doc.type === docType.type);
      } else {
        delete docType.documents;
        docType.needsDocument = true;
      }
    } else {
      docType.documents.splice(docIndex, 1);
    }

    this.orderEntryDocumentsService.addDeletedDocument(documentId);
    this.orderEntryDocumentsService.documents = documents;

    // update row data
    this.gaAgGridService.setRowData(this.gridId, this.orderEntryDocumentsService.documents);
  };

  private getAllowedDocuments(documents:IDocumentGroup[]):IDocumentTypeModel[] {
    const allowedDocs:IDocumentTypeModel[] = [];

    _.forEach(documents, (docType:IDocumentGroup) => {
      if (docType.attachmentTypeId) {
        allowedDocs.push({
          id: docType.attachmentTypeId,
          value: docType.type,
          attachmentTypeId: docType.attachmentTypeId,
          caveatDefinitionId: docType.caveatDefinitionId
        });
      }
    });

    return allowedDocs;
  }

  private onNewDocumentAdded = (model:IAddDocumentModel):void => {
    const typeId:string = model.documentTypeHidden;
    const caveatId:number = model.caveatDefinitionId;

    const currentUser:IUserData = this.authService.getCurrentUser();

    const newDoc:IOrderEntryDocument = {
      deleteAllowed: true,
      name: model.name,
      id: model.documentId,
      author: currentUser,
      date: parseFloat(moment().format('x')),
      uid: model.documentId
    };

    const documents:IDocumentGroup[] = _.clone(this.orderEntryDocumentsService.documents);

    let typeToUpdate:IDocumentGroup = _.findWhere(documents, {
      attachmentTypeId: typeId,
      caveatDefinitionId: caveatId
    }) || _.findWhere(documents, { type: 'Other Documents' });

    if (!typeToUpdate) {
      typeToUpdate = {
        key: 'Other Documents',
        needsDocument: false,
        orderEntryDocumentClass: 'OTHER_DOCUMENTS',
        type: 'Other Documents',
        uploadAllowed: true,
        attachmentTypeId: typeId,
        caveatDefinitionId: caveatId,
        comment: undefined,
        id: <string>model.documentType.id,
        documents: [ newDoc ]
      };
      documents.push(typeToUpdate);
    } else {
      typeToUpdate.documents = typeToUpdate.documents || [];
      typeToUpdate.documents.push(newDoc);
      typeToUpdate.needsDocument = false;
    }

    this.orderEntryDocumentsService.addAddedDocument(model.documentId, caveatId);
    this.orderEntryDocumentsService.documents = documents;
  };

  private getCellClass = (params:CellClassParams):string[] => {
    if (params.node.expanded) {
      return [
        'hide-cell-value',
        'multi-line-cell'
      ];
    }

    return [ 'multi-line-cell' ];
  };
}